import React from 'react';
import { MapPin } from 'lucide-react';

// BUNDLE ISOLATION: the ONLY spatial-decode import reachable from the eager grid
// graph is the header sniffer. Never import decodeSpatialValue / wkx / the lazy
// chunk here — that would pull the decoder into the initial bundle. (SpatialPeekPopover
// reaches the decoder/map only through React.lazy + the async decode API.)
import { summarizeSpatialCell } from '../../../spatial/decode/spatialSniff';
import {
  formatDeclaredSpatialSummary,
  resolveSpatialCellInput,
  type SpatialCellInput,
  type SpatialColumnDeclaration,
} from '../../utils/spatialValueAccessor';
import { SpatialPeekPopover } from '../filters/SpatialPeekPopover';
import type { DatabaseEngineId } from '../../../../../core/engines/engineTypes';

export type SpatialCellRendererProps = {
  value: unknown;
  columnId: string;
  columnName: string;
  rowData: Record<string, unknown> | undefined;
  engine: DatabaseEngineId;
  dataType: string | undefined;
  nativeSpatialColumnIds: Set<string>;
  declaredSpatialColumnIds?: Set<string>;
  /** columnId → declaration (T18). Powers the declared arm's row-aware assembly + peek decode. */
  declaredSpatialColumns?: Map<string, SpatialColumnDeclaration>;
  /** Opens the expanded-dialog map pane for the resolved cell input (T11). */
  onOpenFull: (input: SpatialCellInput) => void;
  /**
   * Windowed grids only: fetch the FULL untruncated value (T26 bounded batch).
   * Absent on non-windowed grids and on already-complete marker objects, where
   * the resolved cell value is decoded directly.
   */
  fetchFullValue?: () => Promise<unknown>;
  /** Resolve non-bundled SRIDs from this connection's `spatial_ref_sys` (T12 ladder). */
  connectionId?: string;
  database?: string;
};

/**
 * Shared spatial cell decoration. Same null-fallthrough contract as
 * `JsonCellRenderer`: returns null when the column isn't a spatial cell (or the
 * value doesn't sniff as geometry) so the caller falls through to its default
 * rendering. When the column IS spatial, renders the sniffer-derived summary
 * (e.g. "POINT · 1 pt · SRID 4326") + a ⌖ peek glyph that opens the map peek
 * popover (T11).
 *
 * The summary comes from the EAGER header sniffer only — never a full decode.
 * The peek popover reaches the decoder/map lazily (React.lazy + async API).
 */
export function SpatialCellRenderer({
  value,
  columnId,
  columnName,
  rowData,
  engine,
  dataType,
  nativeSpatialColumnIds,
  declaredSpatialColumnIds,
  declaredSpatialColumns,
  onOpenFull,
  fetchFullValue,
  connectionId,
  database,
}: SpatialCellRendererProps): React.ReactNode | null {
  const isNative = nativeSpatialColumnIds.has(columnId);
  const isDeclared = declaredSpatialColumnIds?.has(columnId) ?? false;
  if (!isNative && !isDeclared) return null;

  // Row-aware access rule: resolve geometry input through the accessor rather
  // than reading the cell value directly. For a declared lat-lng pair the value
  // is `{ lat, lng }` assembled from BOTH columns; `columnId` keys the row
  // (id === key in both grids), so a native cell resolves to the grid value.
  const declaration = declaredSpatialColumns?.get(columnId);
  const input = resolveSpatialCellInput(columnId, rowData, {
    engine,
    nativeSpatialColumnIds,
    columnDataTypes: dataType != null ? new Map([[columnId, dataType]]) : new Map(),
    declaredSpatialColumns,
  });
  const resolved = input ? input.value : value;
  if (resolved === null || resolved === undefined) return null;

  // Declared cells get an eager label (the sniffer can't read a pair/WKT string);
  // native cells use the wire sniffer summary and fall through when it isn't spatial.
  const summary = declaration
    ? formatDeclaredSpatialSummary(declaration, resolved)
    : summarizeSpatialCell(engine, resolved, dataType);
  if (!summary) return null; // doesn't look spatial → fall through (plain-string fallback)

  return (
    // `w-[240px] max-w-full`: under `table-layout: auto` (the default, unresized
    // column state) a bare flex row has no bounded intrinsic size, so the browser's
    // auto-layout column-width algorithm grows the column to fit the full summary
    // string (e.g. "LINESTRING · 4 pts · SRID 4326") instead of respecting the
    // inner `truncate` span — the column, and thus the table, ends up wider than
    // its viewport and a long summary appears hard-clipped mid-glyph with no
    // ellipsis (only the horizontal scroll edge, not real truncation). A definite
    // `width` on this element (not `max-width` alone — verified `max-width` alone
    // is inert here because the auto-layout intrinsic-size query ignores it once a
    // flex/inline-block descendant is involved) caps that growth; `max-w-full`
    // keeps it shrinking properly once the column IS externally constrained
    // (`table-layout: fixed`, e.g. after a manual column resize).
    <span className="flex items-center gap-1 min-w-0 w-[240px] max-w-full -mx-3 px-3 bg-emerald-500/[0.04] dark:bg-emerald-400/[0.05]">
      <SpatialPeekPopover
        value={resolved}
        engine={engine}
        dataType={dataType}
        declaration={declaration}
        columnName={columnName}
        fetchFullValue={declaration ? undefined : fetchFullValue}
        connectionId={connectionId}
        database={database}
        onOpenFull={() => onOpenFull(input ?? { engine, value: resolved, dataType })}
        trigger={
          <button
            type="button"
            className="inline-flex h-4 w-4 items-center justify-center rounded text-emerald-600/70 dark:text-emerald-400/70 hover:text-emerald-600 dark:hover:text-emerald-400 hover:bg-muted/60 flex-shrink-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
            title="View geometry"
            aria-label="View geometry"
            onClick={(event) => {
              event.stopPropagation();
            }}
            onDoubleClick={(event) => {
              event.stopPropagation();
            }}
            data-testid="cell-spatial-btn"
          >
            <MapPin className="h-3 w-3" aria-hidden="true" />
          </button>
        }
      />
      <span title={summary} className="truncate min-w-0 text-xs">
        {summary}
      </span>
    </span>
  );
}
